import React from "react";
import PaginationComponent from "components/common/Pagination/index";
import AvatarGroup from "components/common/Avatar/AvatarGroup";
import {
  applyAvatarImageFallback,
  baseURLImage,
  capitalizeAllLetters,
  getNameProfile,
} from "Utils/utils";
import TimesheetAiStatusIcon from "../../TimeSheetDetailsSection/ai/components/TimesheetAiStatusIcon";
import TimesheetStatusPill from "../../components/TimesheetRevamp/TimesheetStatusPill";
import { BULK_PAGE_SIZE, RUN_STATUS } from "../model/bulkAnalyseModel";

const STATUS_PILL = {
  [RUN_STATUS.SAVED]: "bg-[#ECFDF5] text-[#047857]",
  [RUN_STATUS.FAILED]: "bg-[#FEE2E2] text-[#DC2626]",
  [RUN_STATUS.RUNNING]: "bg-[#EEF2FB] text-[#1D4ED8]",
  [RUN_STATUS.QUEUED]: "bg-[#F8FAFC] text-[#475569]",
  [RUN_STATUS.LEFT_OUT]: "bg-[#F8FAFC] text-[#64748B]",
  [RUN_STATUS.STOPPED]: "bg-[#FFF7ED] text-[#9A3412]",
};

const STATUS_LABEL = {
  [RUN_STATUS.SAVED]: "Completed",
  [RUN_STATUS.FAILED]: "Failed",
  [RUN_STATUS.RUNNING]: "Running",
  [RUN_STATUS.QUEUED]: "Queued",
  [RUN_STATUS.LEFT_OUT]: "Left out",
  [RUN_STATUS.STOPPED]: "Paused",
};

const personName = (person) =>
  capitalizeAllLetters(
    `${person?.first_name || ""} ${person?.last_name || ""}`.trim()
  );

const isInactive = (status) => {
  if (status == null || status === "") return false;
  if (status === false) return true;
  const value = String(status).toLowerCase();
  return value === "inactive" || value === "0" || value === "false";
};

function PersonAvatar({ person, photoKey }) {
  const first = person?.first_name || "";
  const last = person?.last_name || "";
  const photo = person?.[photoKey];
  return (
    <img
      alt=""
      onError={(event) => applyAvatarImageFallback(event, first, last)}
      className="h-full w-full rounded-full object-cover"
      src={photo ? baseURLImage(photo) : getNameProfile(first, last)}
    />
  );
}

function WorkerCell({ profile, fallbackName }) {
  const person = profile || null;
  const name = person ? personName(person) : capitalizeAllLetters(fallbackName);
  const inactive = person ? isInactive(person.status) : false;
  return (
    <div
      className={`flex min-w-0 items-center gap-2 ${inactive ? "text-red-500 opacity-50" : ""}`}
      title={inactive ? `${name} (Inactive)` : name}
    >
      <div className="h-8 w-8 shrink-0">
        <PersonAvatar person={person || { first_name: name }} photoKey="photo" />
      </div>
      <span className="min-w-0 flex-1 truncate text-xs font-semibold capitalize text-[#0F172A]">
        {name || "—"}
      </span>
    </div>
  );
}

function ParticipantCell({ participants, fallbackName }) {
  const people = Array.isArray(participants) ? participants : [];
  if (people.length === 1) {
    const person = people[0];
    const name = personName(person);
    const inactive = person?.status != null && person.status !== "" && person.status !== "active";
    return (
      <div
        className={`flex min-w-0 items-center gap-1 ${inactive ? "text-red-500 opacity-50" : ""}`}
        title={inactive ? `${name} (Inactive)` : name}
      >
        <div className="h-9 w-9 shrink-0">
          <PersonAvatar person={person} photoKey="profile_picture" />
        </div>
        <span className="min-w-0 flex-1 truncate text-xs capitalize text-[#0F172A]">
          {name}
        </span>
      </div>
    );
  }
  if (people.length > 1) {
    return (
      <AvatarGroup
        type="participant"
        arrayData={people}
        cssClass="mt-0"
        maxLength={3}
        isNoRing
        popoverZIndex={10080}
      />
    );
  }
  return (
    <span className="text-xs capitalize text-[#0F172A]">
      {capitalizeAllLetters(fallbackName) || "—"}
    </span>
  );
}

function Bone({ className = "" }) {
  return <span className={`inline-block animate-pulse rounded bg-[#E2E8F0] ${className}`} />;
}

function SkeletonRows() {
  return Array.from({ length: 6 }).map((_, index) => (
    <tr key={`skeleton-${index}`} className="border-t border-[#E5E7EB]" aria-hidden="true">
      <td className="px-2.5 py-2.5">
        <div className="flex items-center gap-2">
          <Bone className="h-8 w-8 shrink-0 rounded-full" />
          <Bone className="h-3 w-24" />
        </div>
      </td>
      <td className="px-2.5 py-2.5">
        <div className="flex items-center gap-2">
          <Bone className="h-8 w-8 shrink-0 rounded-full" />
          <Bone className="h-3 w-20" />
        </div>
      </td>
      <td className="px-2.5 py-2.5">
        <div className="flex flex-col gap-1.5">
          <Bone className="h-3 w-16" />
          <Bone className="h-3 w-14" />
        </div>
      </td>
      <td className="px-2.5 py-2.5">
        <Bone className="h-6 w-16 rounded-full" />
      </td>
      <td className="px-2.5 py-2.5">
        <div className="flex items-center gap-2">
          <Bone className="h-7 w-7 shrink-0 rounded-full" />
          <Bone className="h-3 w-8" />
        </div>
      </td>
    </tr>
  ));
}

function ShiftCell({ shift }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-semibold capitalize text-[#0F172A]">{shift?.day || "—"}</span>
        <span className="text-xs font-medium text-[#64748B]">{shift?.date || "—"}</span>
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-semibold text-[#0F172A]">{shift?.scheduleStart || "—"}</span>
        <span className="text-xs font-medium text-[#64748B]">{shift?.scheduleEnd || "—"}</span>
      </div>
    </div>
  );
}

export function BulkAnalyseTable({
  rows,
  page,
  totalPages,
  total,
  onPageChange,
  onOpenReport,
  showRunStatus = false,
  loading = false,
  className = "",
}) {
  return (
    <div className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#E5E7EB] ${className}`}>
      <div className="bulk-analyse-table-scroll min-h-0 min-w-0 flex-1 overscroll-contain">
      <table className="min-w-[860px] w-full table-fixed" aria-busy={loading}>
        <colgroup>
          <col className="w-[22%]" />
          <col className="w-[24%]" />
          <col className="w-[24%]" />
          <col className="w-[14%]" />
          <col className="w-[16%]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-[#F8FAFC]">
          <tr>
            {["Worker", "Participant", "Shift", "Status", "AI"].map((label) => (
              <th
                key={label}
                className="h-9 bg-[#F8FAFC] px-2.5 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-[#0F172A]"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody aria-busy={loading}>
          {loading ? (
            <SkeletonRows />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-sm text-[#64748B]">
                No timesheets match this filter.
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const statusKey = showRunStatus ? row.runStatus : null;
              return (
                <tr key={row.id} className="border-t border-[#E5E7EB]">
                  <td className="px-2.5 py-2.5 align-middle">
                    <WorkerCell profile={row.workerProfile} fallbackName={row.worker} />
                  </td>
                  <td className="px-2.5 py-2.5 align-middle">
                    <ParticipantCell participants={row.participants} fallbackName={row.participant} />
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-2.5 align-middle">
                    <ShiftCell shift={row.shift} />
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-2.5 align-middle">
                    {statusKey ? (
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                          STATUS_PILL[statusKey] || "bg-[#F8FAFC] text-[#64748B]"
                        }`}
                        title={statusKey === RUN_STATUS.FAILED ? row.errorMessage || undefined : undefined}
                      >
                        {STATUS_LABEL[statusKey] || statusKey}
                      </span>
                    ) : (
                      <TimesheetStatusPill status={row.timesheetStatus} />
                    )}
                  </td>
                  <td className="px-2.5 py-2.5 align-middle" onClick={(event) => event.stopPropagation()}>
                    {row.aiStatus ? (
                      <TimesheetAiStatusIcon
                        timesheetId={Number(row.timesheetId)}
                        status={row.aiStatus}
                        onOpenReport={() => onOpenReport?.(row)}
                      />
                    ) : (
                      <span className="pl-2 text-sm font-medium text-[#94A3B8]">-</span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>
      <div className="bulk-analyse-table-pager shrink-0">
        <PaginationComponent
          variant="compact"
          page={page}
          activeListLength={rows}
          limit={BULK_PAGE_SIZE}
          totalList={total}
          totalPages={totalPages}
          handlePagination={onPageChange}
          pageRangeDisplayed={5}
          marginPagesDisplayed={2}
          dense
        />
      </div>
    </div>
  );
}
